import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material';
import { DateTime, Info } from 'luxon';
import { cloneDeep } from 'lodash';

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array(length);
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}

export const MAT_LUXON_DATE_FORMATS = {
  parse: {dateInput: 'dd/LL/yyyy'},
  display: {dateInput: 'dd/LL/yyyy', monthYearLabel: 'LLL yyyy', dateA11yLabel: 'LL', monthYearA11yLabel: 'LLL yyyy'}
};

@Injectable()
export class LuxonDateAdapter extends DateAdapter<DateTime> {

  addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({day: days});
  }

  addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({month: months});
  }

  addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({year: years});
  }

  clone(date: DateTime): DateTime {
    return cloneDeep(date);
  }

  createDate(year: number, month: number, date: number): DateTime {
    return DateTime.fromObject({year: year, month: month + 1, day: date});
  }

  format(date: DateTime, displayFormat: any): string {
    return date.toFormat(displayFormat);
  }

  getDate(date: DateTime): number {
    return date.day;
  }

  getDateNames(): string[] {
    return range(31, (i) => this.createDate(2017, 0, i + 1).toFormat('d'));
  }

  getDayOfWeek(date: DateTime): number {
    return date.weekday % 7;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const weekdays = Info.weekdays(style);
    weekdays.unshift(weekdays.pop());
    return weekdays;
  }

  getFirstDayOfWeek(): number {
    return 0;
  }

  getMonth(date: DateTime): number {
    return date.month - 1;
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Info.months(style);
  }

  getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth;
  }

  getYear(date: DateTime): number {
    return date.year;
  }

  getYearName(date: DateTime): string {
    return date.toFormat('y');
  }

  invalid(): DateTime {
    return DateTime.invalid('This is invalid');
  }

  isDateInstance(obj: any): boolean {
    return obj instanceof DateTime;
  }

  isValid(date: DateTime): boolean {
    return date.isValid;
  }

  parse(value: any, parseFormat: any): DateTime | null {
    if (!value) return null;

    if (typeof value === 'string' && parseFormat)
      return DateTime.fromFormat(value, parseFormat);
    else if (typeof value === 'string')
      return DateTime.fromFormat(value, 'dd/MM/yyyy', {});
    else if (typeof value === 'object') {
      return DateTime.fromObject(value);
    } else return null;
  }

  toIso8601(date: DateTime): string {
    return date.toISO();
  }

  today(): DateTime {
    return DateTime.local();
  }
}



// WEBPACK FOOTER //
// ./src/pages/shared/components/pt-luxon-date-picker/LuxonDateAdapter.ts